import type { ReactNode } from 'react';
import { motion, type Transition } from 'framer-motion';
import { clsx } from 'clsx';
import { pulse, shouldReduceMotion } from '@/lib/animations/optimized-animations';

export type GradientBadgeVariant = 'cool' | 'hot' | 'green' | 'violet' | 'amber';

export interface GradientBadgeProps {
  /** Badge label / content. */
  children?: ReactNode;
  /** Color role for the border + text. cool=cyan, hot=orange, green=green, violet=violet, amber=coming-soon "bald". */
  variant?: GradientBadgeVariant;
  /** Optional leading icon rendered before the label. */
  icon?: ReactNode;
  /** Extra classes merged onto the pill. */
  className?: string;
}

const VARIANT_TOKEN: Record<GradientBadgeVariant, string> = {
  cool: 'var(--cyan)',
  hot: 'var(--orange)',
  green: 'var(--green)',
  violet: 'var(--violet)',
  amber: 'var(--amber)',
};

/**
 * GradientBadge — a rounded-full pill whose border and text adopt a role-mapped
 * accent token, on a subtle dark-glass fill. Mounts with a gentle pulse unless
 * reduced motion is preferred, in which case it renders statically.
 */
export function GradientBadge({
  children,
  variant = 'cool',
  icon,
  className,
}: GradientBadgeProps) {
  const reduce = shouldReduceMotion();
  const accent = VARIANT_TOKEN[variant];

  return (
    <motion.span
      animate={reduce ? undefined : pulse.animate}
      transition={reduce ? undefined : (pulse.transition as Transition)}
      data-variant={variant}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1',
        'text-xs font-medium leading-none bg-[var(--panel)]/60 backdrop-blur-sm',
        className,
      )}
      style={{
        borderColor: accent,
        color: accent,
      }}
    >
      {icon != null && (
        <span aria-hidden="true" className="inline-flex shrink-0 items-center">
          {icon}
        </span>
      )}
      {children}
    </motion.span>
  );
}
